import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Button,
  Divider,
  Tabs,
  Tab,
  Card,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import NotesIcon from "@mui/icons-material/Notes";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { PermissionsContext } from "../../utils/PermissionsContext";
import { useUser } from "../UserContext";
import api from "../../utils/api";

// Subcomponents
import AddWorkOrder from "./addWorkorder";
import AddArchitecturalRequest from "./AddArchitecturalRequest";
import ArchitecturalRequestByMember from "./ArchitecturalRequestbyMember";
import MemberDetailsCard from "./MemberDetailsCard";
import ActivityTimeline from "./ActivityTimeline";
import ViolationsList from "./ViolationsList";
import AddViolationForm from "./AddViolationForm";
import WorkOrdersList from "./WorkOrdersList";
import GateVisitorsList from "./GateVisitorsList";
import AddGateVisitorForm from "./AddGateVisitorForm";
import MemberAnalyticsPanel from "./MemberAnalyticsPanel";
import OwnerNotesTab from "./OwnerNotesTab";

const MemberProfilePage = () => {
  const { ClubMemberCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fromPage = queryParams.get("from");
  const { user } = useUser();
  const permissionsCtx = useContext(PermissionsContext);

  const hasPermission =
    typeof permissionsCtx === "function"
      ? permissionsCtx
      : (perm) =>
          Array.isArray(permissionsCtx) && permissionsCtx.includes(perm);

  const [member, setMember] = useState(null);
  const [profileSource, setProfileSource] = useState("member"); // "member" | "property"
  const [violations, setViolations] = useState([]);
  const [violationTypes, setViolationTypes] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);
  const [architecturalRequests, setArchitecturalRequests] = useState([]);
  const [jonasNotes, setJonasNotes] = useState([]);

  // Inline form toggles
  const [showAddViolation, setShowAddViolation] = useState(false);
  const [showAddWorkOrder, setShowAddWorkOrder] = useState(false);
  const [showAddArchitecturalRequest, setShowAddArchitecturalRequest] =
    useState(false);
  const [showAddGateVisitor, setShowAddGateVisitor] = useState(false);

  const [formData, setFormData] = useState({
    property_id: ClubMemberCode || "",
    account_id: "",
    ccr_violation_type_id: "",
    inspection_id: "1",
    cure_by:
      `${localStorage.getItem("firstName") || ""} ${
        localStorage.getItem("lastName") || ""
      }`.trim() || "Unknown User",
    comment: "",
    upload_urls: null,
    violated_at: "",
  });

  const fetchNotes = useCallback(async () => {
    try {
      const res = await api.get(`/members/${ClubMemberCode}/notes`);
      setJonasNotes(res.data.notes || []);
    } catch (err) {
      console.error("Failed to fetch notes:", err);
      setJonasNotes([]);
    }
  }, [ClubMemberCode]);

  // member first, fallback to property
  const fetchMemberOrProperty = useCallback(async () => {
    const normalizePropertyToMember = (property) => ({
      ...property,
      ClubMemberCode: (property.account_id ?? `${property.id ?? ""}`).toString(),
      isPropertyFallback: true,
    });

    try {
      const memberRes = await api.get(`/members/${ClubMemberCode}`);
      setProfileSource("member");
      return memberRes.data;
    } catch (err) {
      const status = err?.response?.status;

      if (status && status !== 404) {
        throw err;
      }

      console.warn(
        `Member ${ClubMemberCode} not found. Falling back to property.`
      );

      const propRes = await api.get(`/properties/${ClubMemberCode}`);
      setProfileSource("property");
      return normalizePropertyToMember(propRes.data);
    }
  }, [ClubMemberCode]);

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);

      const memberData = await fetchMemberOrProperty();

      const [
        violationTypesRes,
        violationsRes,
        statusesRes,
        workOrdersRes,
        acrRes,
      ] = await Promise.all([
        api.get(`/getCcrViolationTypes`),
        api.get(`/getViolations`, { params: { account_id: ClubMemberCode } }),
        api.get(`/statuses`),
        api.get(`/getWorkOrders`, { params: { property_id: ClubMemberCode } }),
        api.get(`/architectural-requests/member/${ClubMemberCode}`),
      ]);

      setMember(memberData);
      setViolationTypes(violationTypesRes.data || []);
      setViolations(violationsRes.data || []);
      setStatuses(statusesRes.data || []);
      setWorkOrders(workOrdersRes.data || []);
      setArchitecturalRequests(acrRes.data || []);

      const now = new Date();
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(now.getDate() - 14);

      const recentViolations = (violationsRes.data || [])
        .filter(
          (v) =>
            new Date(v.updated_at) >= twoWeeksAgo &&
            new Date(v.updated_at) <= now
        )
        .map((v) => ({
          date: v.updated_at,
          message: `Violation ${String(v.status || "").toUpperCase()}`,
        }));

      const recentWorkOrders = (workOrdersRes.data || [])
        .filter(
          (o) =>
            new Date(o.updated_at) >= twoWeeksAgo &&
            new Date(o.updated_at) <= now
        )
        .map((o) => ({
          date: o.updated_at,
          message: `Work Order ${
            o.resolved_at ? "Resolved" : "Unresolved"
          }: ${o.description || "No description available"}`,
        }));

      setActivity(
        [...recentViolations, ...recentWorkOrders].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        )
      );

      setFormData((prev) => ({
        ...prev,
        property_id: memberData?.ClubMemberCode || ClubMemberCode,
        account_id: memberData?.ClubMemberCode || ClubMemberCode,
      }));
    } catch (err) {
      console.error(err);
      setError("Failed to load member/property profile.");
    } finally {
      setLoading(false);
    }
  }, [ClubMemberCode, fetchMemberOrProperty]);

  const refreshViolations = useCallback(async () => {
    try {
      const res = await api.get(`/getViolations`, {
        params: { account_id: ClubMemberCode },
      });
      setViolations(res.data || []);
    } catch (err) {
      console.error("Failed to refresh violations:", err);
    }
  }, [ClubMemberCode]);

  const refreshWorkOrders = useCallback(async () => {
    try {
      const res = await api.get(`/getWorkOrders`, {
        params: { property_id: ClubMemberCode },
      });
      setWorkOrders(res.data || []);
    } catch (err) {
      console.error("Failed to refresh work orders:", err);
    }
  }, [ClubMemberCode]);

  const refreshArchitecturalRequests = useCallback(async () => {
    try {
      const res = await api.get(
        `/architectural-requests/member/${ClubMemberCode}`
      );
      setArchitecturalRequests(res.data || []);
    } catch (err) {
      console.error("Failed to refresh architectural requests:", err);
    }
  }, [ClubMemberCode]);

  useEffect(() => {
    fetchAllData();
    fetchNotes();
  }, [fetchAllData, fetchNotes]);

  const handleTabChange = async (e, newIndex) => {
    setTabIndex(newIndex);
    const selectedTab = visibleTabs[newIndex]?.label;

    if (selectedTab === "Violations") {
      refreshViolations();
    } else if (selectedTab === "Work Orders") {
      refreshWorkOrders();
    } else if (selectedTab === "Architectural Request") {
      refreshArchitecturalRequests();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, upload_urls: file }));

    if (file) {
      const reader = new FileReader();
      reader.onload = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewImage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.property_id ||
      !formData.ccr_violation_type_id ||
      !formData.violated_at
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) =>
      formDataToSend.append(key, value)
    );

    try {
      await api.post(`/addViolation`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Violation added successfully.");
      await refreshViolations();
      setShowAddViolation(false);

      setFormData((prev) => ({
        ...prev,
        ccr_violation_type_id: "",
        comment: "",
        violated_at: "",
        upload_urls: null,
      }));
      setPreviewImage(null);
    } catch (err) {
      console.error("Failed to add violation:", err);
      alert("Failed to add violation.");
    }
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const statCardSx = {
    ...pageCardSx,
    p: 2.25,
    height: "100%",
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const primaryActionButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    whiteSpace: "nowrap",
    boxShadow: "none",
  };

  const openWorkOrdersCount = useMemo(() => {
    return workOrders.filter(
      (w) =>
        !["resolved", "closed", "completed"].includes(
          String(w.status || "").toLowerCase()
        )
    ).length;
  }, [workOrders]);

  const openViolationsCount = useMemo(() => {
    return violations.filter(
      (v) =>
        !["closed", "violation cured", "withdrawn / canceled"].includes(
          String(v.status || "").toLowerCase()
        )
    ).length;
  }, [violations]);

  const summaryCards = [
    {
      title: "Member Code",
      value: member?.ClubMemberCode || "—",
      subtitle:
        profileSource === "property" ? "Loaded from property" : "Current member",
      icon: <PersonIcon sx={{ color: "#2f63ad" }} />,
    },
    {
      title: "Open Violations",
      value: openViolationsCount,
      subtitle: `${violations.length} total`,
      icon: <WarningAmberIcon sx={{ color: "#d97706" }} />,
    },
    {
      title: "Open Work Orders",
      value: openWorkOrdersCount,
      subtitle: `${workOrders.length} total`,
      icon: <AssignmentIcon sx={{ color: "#2f63ad" }} />,
    },
    {
      title: "Architectural Requests",
      value: architecturalRequests.length,
      subtitle: "Total requests",
      icon: <ArchitectureIcon sx={{ color: "#2f63ad" }} />,
    },
    {
      title: "Owner Notes",
      value: jonasNotes.length,
      subtitle: "Notes available",
      icon: <NotesIcon sx={{ color: "#2f63ad" }} />,
    },
  ];

  const renderSectionHeader = ({
    title,
    subtitle,
    buttonLabel,
    cancelLabel = "Cancel",
    showForm,
    setShowForm,
    canAdd,
  }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
        flexWrap: "wrap",
        gap: 1.5,
      }}
    >
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
          {subtitle}
        </Typography>
      </Box>

      {canAdd && (
        <Button
          variant={showForm ? "outlined" : "contained"}
          onClick={() => setShowForm((prev) => !prev)}
          sx={primaryActionButtonSx}
        >
          {showForm ? cancelLabel : buttonLabel}
        </Button>
      )}
    </Box>
  );

  const tabConfig = [
    {
      label: "Owner Notes",
      component: (
        <>
          {profileSource === "property" && (
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 1 }}
              color="warning.main"
            >
              Loaded from Property record (member not found)
            </Typography>
          )}
          <OwnerNotesTab memberCode={member?.ClubMemberCode} />
        </>
      ),
      permission: "view_violations",
    },
    {
      label: "Violations",
      component: (
        <>
          {renderSectionHeader({
            title: "Violations History",
            subtitle: "Review existing violations and add a new violation.",
            buttonLabel: "+ Add Violation",
            showForm: showAddViolation,
            setShowForm: setShowAddViolation,
            canAdd: hasPermission("add_violation"),
          })}

          <Divider sx={{ mb: 2 }} />

          {showAddViolation && hasPermission("add_violation") && (
            <Box sx={{ mb: 3 }}>
              <AddViolationForm
                formData={formData}
                setFormData={setFormData}
                handleInputChange={handleInputChange}
                handleFileChange={handleFileChange}
                handleSubmit={handleSubmit}
                previewImage={previewImage}
                violationTypes={violationTypes}
                propertyId={member?.ClubMemberCode?.trim()}
                accountId={member?.ClubMemberCode?.trim()}
              />
            </Box>
          )}

          <ViolationsList
            violations={violations}
            violationTypes={violationTypes}
            navigate={navigate}
          />
        </>
      ),
      permission: "view_violations",
    },
    {
      label: "Work Orders",
      component: (
        <>
          {renderSectionHeader({
            title: "Work Orders",
            subtitle: "Review work orders and create a new work order.",
            buttonLabel: "+ Add Work Order",
            showForm: showAddWorkOrder,
            setShowForm: setShowAddWorkOrder,
            canAdd: hasPermission("view_work_orders"),
          })}

          <Divider sx={{ mb: 2 }} />

          {showAddWorkOrder && hasPermission("view_work_orders") && (
            <Box sx={{ mb: 3 }}>
              <AddWorkOrder propertyId={member?.ClubMemberCode} />
            </Box>
          )}

          <WorkOrdersList
            workOrders={workOrders}
            statuses={statuses}
            navigate={navigate}
            clubMemberCode={ClubMemberCode}
          />
        </>
      ),
      permission: "view_work_orders",
    },
    {
      label: "Architectural Request",
      component: (
        <>
          {renderSectionHeader({
            title: "Architectural Request",
            subtitle:
              "Review architectural requests and submit a new request.",
            buttonLabel: "+ Add Architectural Request",
            showForm: showAddArchitecturalRequest,
            setShowForm: setShowAddArchitecturalRequest,
            canAdd: hasPermission("add_architectural_request"),
          })}

          <Divider sx={{ mb: 2 }} />

          {showAddArchitecturalRequest &&
            hasPermission("add_architectural_request") && (
              <Box sx={{ mb: 3 }}>
                <AddArchitecturalRequest
                  propertyId={member?.ClubMemberCode}
                  accountId={member?.ClubMemberCode}
                />
              </Box>
            )}

          <ArchitecturalRequestByMember
            propertyId={member?.ClubMemberCode?.trim()}
            accountId={member?.ClubMemberCode?.trim()}
          />
        </>
      ),
      permission: "view_architectural_requests",
    },
    {
      label: "Gate Visitors",
      component: (
        <>
          {renderSectionHeader({
            title: "Gate Visitors",
            subtitle: "Review visitors and add a new gate visitor.",
            buttonLabel: "+ Add Gate Visitor",
            showForm: showAddGateVisitor,
            setShowForm: setShowAddGateVisitor,
            canAdd: hasPermission("add_gate_visitor"),
          })}

          <Divider sx={{ mb: 2 }} />

          {showAddGateVisitor && hasPermission("add_gate_visitor") && (
            <Box sx={{ mb: 3 }}>
              <AddGateVisitorForm clubMemberCode={member?.ClubMemberCode} />
            </Box>
          )}

          <GateVisitorsList clubMemberCode={member?.ClubMemberCode} />
        </>
      ),
      permission: "view_gate_visitors",
    },
  ];

  const visibleTabs = tabConfig.filter(
    ({ permission }) => !permission || hasPermission(permission)
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "40vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ textAlign: "center", mt: 4 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ mb: 2.5 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            fromPage === "properties"
              ? navigate("/home/properties")
              : navigate("/home/clubmembers")
          }
          sx={secondaryButtonSx}
        >
          {fromPage === "properties" ? "Back to Properties" : "Back to Members"}
        </Button>
      </Box>

      <Box sx={{ mb: 3.5 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#223142",
            mb: 0.5,
          }}
        >
          {profileSource === "property" ? "Property Profile" : "Member Profile"}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
          }}
        >
          View member details, activity, analytics, notes, and related account
          actions.
        </Typography>

        {profileSource === "property" && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1 }}
            color="warning.main"
          >
            Loaded from Property record because no matching member was found.
          </Typography>
        )}
      </Box>

      <Card sx={{ ...pageCardSx, p: { xs: 1.75, sm: 2, md: 2.5 }, mb: 3 }}>
        <MemberDetailsCard member={member} notes={jonasNotes} />
      </Card>

      <Grid container spacing={2.5} sx={{ mb: 6 }}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.title}>
            <Card sx={statCardSx}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                    {card.title}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: "#223142",
                      mb: 0.5,
                      wordBreak: "break-word",
                    }}
                  >
                    {card.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#8a94a6" }}>
                    {card.subtitle}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(47,99,173,0.08)",
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ ...pageCardSx, overflow: "hidden", mb: 3 }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.25,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.5,
            }}
          >
            Account Activity
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
              mb: 2,
            }}
          >
            Review member notes, violations, work orders, architectural requests,
            and visitors.
          </Typography>

          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: "auto",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                minHeight: 40,
                color: "#5f6b7a",
                mr: 1,
                px: { xs: 1.5, md: 2 },
                py: 1,
                whiteSpace: "nowrap",
                minWidth: "fit-content",
              },
              "& .Mui-selected": {
                color: "#2f63ad !important",
                backgroundColor: "rgba(47,99,173,0.08)",
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            {visibleTabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          {visibleTabs.length > 0 ? (
            visibleTabs[tabIndex]?.component
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}
            >
              You do not have access to any member-related actions.
            </Typography>
          )}
        </Box>
      </Card>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Card sx={{ ...pageCardSx, p: 2.5, height: "100%" }}>
            <ActivityTimeline activity={activity} />
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...pageCardSx, p: 2.5, height: "100%" }}>
            <MemberAnalyticsPanel
              violations={violations}
              workOrders={workOrders}
              violationTypes={violationTypes}
              architecturalRequests={architecturalRequests}
            />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MemberProfilePage;